import React from 'react';
import EmailTicket from '../common/EmailTicket';

/**
 * Email Ticket Preview Page
 * Access at: /email-ticket-preview
 * 
 * This allows you to preview how the ticket will look in emails
 * Uses table-based layout with inline styles for email compatibility
 */
const EmailTicketPreview = () => {
  // Mock booking data with Family Saver discount
  const mockBookingReference = 'BEL-COL-6TTUXN-J';
  
  const mockSelectedTickets = [
    {
      id: 1,
      webSaleItemId: '51229dc8-b7e3-42ac-890b-3471c4bdaa83',
      name: 'Parkland Membership (Child)',
      title: 'Parkland Membership (Child)',
      quantity: 1,
      price: 6.60,
      total: 6.60
    }
  ];

  const mockSelectedDate = new Date('2025-11-29');
  
  const mockSelectedTimeSlot = {
    time: '12:00',
    id: 'slot-1200'
  };

  const mockCustomerDetails = {
    firstName: 'Jacob',
    lastName: 'Hartwell',
    email: 'jacobhartwell@hotmail.co.uk',
    phone: '07713120728',
    address: {
      line1: 'Hillmorton Lane',
      city: 'Rugby',
      postcode: 'CV23 0SS'
    }
  };

  const mockTotalAmount = 6.60;

  // Mock booking summary with Family Saver discount
  const mockBookingSummary = {
    totalAmount: 0.033,
    originalPrice: 6.60,
    finalPrice: 0.033,
    discounts: [
      {
        code: 'FamilySaver2',
        discountedAmount: 6.567
      }
    ],
    totalSavings: 6.567
  };

  // Mock Venpos basket
  const mockVenposBasket = {
    data: {
      totalPrice: 6.60,
      finalPrice: 0.033,
      discounts: [
        {
          code: 'FamilySaver2',
          discountedAmount: 6.567
        }
      ],
      groups: [
        {
          items: [
            {
              webSaleItemId: '51229dc8-b7e3-42ac-890b-3471c4bdaa83',
              name: 'Parkland Membership (Child)',
              discountedAmount: 0.033,
              totalPrice: 6.60
            }
          ]
        }
      ]
    }
  };

  return (
    <div style={{ 
      paddingTop: '120px',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      {/* Test Banner */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        border: 'none',
        padding: '1.5rem 2rem',
        margin: '0 auto 2rem',
        maxWidth: '600px',
        borderRadius: '12px',
        textAlign: 'center',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        color: 'white'
      }}>
        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: '600' }}>
          📧 EMAIL TICKET PREVIEW
        </h3>
        <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.6', opacity: 0.95 }}>
          This shows how the ticket will appear in customer emails.
          <br/>
          <strong>Email-safe HTML:</strong> table layout, inline styles, no SVG
          <br/>
          <strong>Family Saver discount (FamilySaver2)</strong> applied for testing
        </p>
      </div>

      {/* Email Container Preview */}
      <div style={{
        backgroundColor: '#ffffff',
        maxWidth: '600px',
        margin: '0 auto 2rem',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <EmailTicket
          bookingReference={mockBookingReference}
          selectedTickets={mockSelectedTickets}
          selectedDate={mockSelectedDate}
          selectedTimeSlot={mockSelectedTimeSlot}
          customerDetails={mockCustomerDetails}
          totalAmount={mockTotalAmount}
          venposBasket={mockVenposBasket}
          bookingSummary={mockBookingSummary}
          isFamilySaver={true}
          qrCodeUrl={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(mockBookingReference)}`}
        />
      </div>

      {/* Info Box */}
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '1.5rem 2rem',
        backgroundColor: '#fff9e6',
        border: '2px solid #ffc107',
        borderRadius: '8px',
        marginBottom: '2rem'
      }}>
        <h4 style={{ 
          margin: '0 0 1rem 0', 
          fontSize: '1.1rem', 
          color: '#101d40',
          fontWeight: '600'
        }}>
          📋 Email Integration Notes
        </h4>
        <ul style={{ 
          margin: 0, 
          paddingLeft: '1.5rem', 
          fontSize: '0.9rem', 
          lineHeight: '1.7',
          color: '#333'
        }}>
          <li><strong>Table-based layout</strong> for universal email client support</li>
          <li><strong>Inline styles only</strong> - no CSS classes or styled-components</li>
          <li><strong>QR Code as PNG image</strong> - hosted on external server or generated server-side</li>
          <li><strong>Absolute image URLs</strong> required (no relative paths)</li>
          <li><strong>Logo must be hosted</strong> at absolute URL (currently uses production URL)</li>
          <li><strong>Tested for:</strong> Gmail, Outlook, Apple Mail, Yahoo, Thunderbird</li>
          <li><strong>Family Saver detection:</strong> Codes starting with "FS" show special branding</li>
        </ul>
      </div>

      {/* HTML Source Button */}
      <div style={{
        maxWidth: '600px',
        margin: '0 auto 3rem',
        textAlign: 'center'
      }}>
        <button
          onClick={() => {
            const emailHtml = document.querySelector('.email-ticket-container')?.innerHTML;
            if (emailHtml) {
              const blob = new Blob([emailHtml], { type: 'text/html' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'email-ticket-template.html';
              a.click();
              URL.revokeObjectURL(url);
            }
          }}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#101d40',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          Download HTML Source
        </button>
      </div>

      {/* Hidden container for HTML export */}
      <div className="email-ticket-container" style={{ display: 'none' }}>
        <EmailTicket
          bookingReference={mockBookingReference}
          selectedTickets={mockSelectedTickets}
          selectedDate={mockSelectedDate}
          selectedTimeSlot={mockSelectedTimeSlot}
          customerDetails={mockCustomerDetails}
          totalAmount={mockTotalAmount}
          venposBasket={mockVenposBasket}
          bookingSummary={mockBookingSummary}
          isFamilySaver={true}
          qrCodeUrl={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(mockBookingReference)}`}
        />
      </div>
    </div>
  );
};

export default EmailTicketPreview;

